/**
 * VH-248: nutrition on a phone — one screen, no sidebar, signed in.
 *
 * Reto, 2026-09-21: "capturing nutrition and reading the nutrition is difficult on mobile. We have
 * to have a simplified version like we have for workout." The full Nutrition page carries search,
 * editing, history and reports; this page does two things: shows today against the targets, and
 * logs a meal in at most three taps — a recent or favourite meal, a planned meal, a photo or a line
 * of text. Every action calls an endpoint the Nutrition page already uses.
 *
 * The day can be flipped back (and forward again, up to today): a meal forgotten yesterday is
 * logged the same way, onto the day shown.
 *
 * Signed in, not token-based like the workout capture sheet: a meal has no printed QR, and a public
 * link would be a capability to write the food log.
 */
import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowTopRightOnSquareIcon, ChevronLeftIcon, ChevronRightIcon, HomeIcon } from '@heroicons/react/24/outline'
import { nutritionAPI, planDocumentAPI, profileAPI } from '../services/api'
import { contentLanguage } from '../i18n/supported'
import { localIsoDate, mealTypeForHour, planForDate, shiftIsoDate, totalsOf } from '../utils/eatToday'
import IntakeSummary from '../components/eat/IntakeSummary'
import NextPlannedMeal from '../components/eat/NextPlannedMeal'
import QuickEntry from '../components/eat/QuickEntry'
import RecentMeals from '../components/eat/RecentMeals'
import GroupTabs from '../components/nav/GroupTabs'

export default function EatToday() {
  const { t, i18n } = useTranslation()
  const queryClient = useQueryClient()
  const today = localIsoDate()
  const [date, setDate] = useState(today)
  const isToday = date === today
  const language = contentLanguage(i18n.language)
  const dayLabel = isToday ? t('eat.today')
    : date === shiftIsoDate(today, -1) ? t('eat.yesterday')
      : new Date(`${date}T12:00:00`).toLocaleDateString(i18n.language, { weekday: 'short', day: 'numeric', month: 'short' })

  const meals = useQuery({
    queryKey: ['eat', 'meals', date],
    queryFn: () => nutritionAPI.getMealsByDate(date).then((r) => r.data?.meals ?? []),
  })
  const goal = useQuery({
    queryKey: ['eat', 'goal', date],
    queryFn: () => profileAPI.getCurrentGoal(date).then((r) => r.data ?? null).catch(() => null),
  })
  // Same key as PlanMealLogger, so the plan is fetched once for both.
  const plans = useQuery({
    queryKey: ['planMeals', language],
    queryFn: () => planDocumentAPI.listForLogging(language).then((r) => r.data),
  })

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ['eat'] })
    queryClient.invalidateQueries({ queryKey: ['planMeals'] })
  }
  const list = meals.data ?? []

  return (
    <main className="min-h-screen bg-surface px-4 py-4 pb-10 max-w-lg mx-auto space-y-4" data-testid="eat-today">
      <header className="flex items-center justify-between gap-3">
        <Link to="/" className="p-2 -ml-2 text-gray-700" aria-label={t('nav.dashboard')}>
          <HomeIcon className="h-6 w-6" aria-hidden="true" />
        </Link>
        <h1 className="text-lg font-semibold text-gray-900">{t('eat.title')}</h1>
        <Link to="/nutrition" className="flex items-center gap-1 text-sm text-teal-800 underline" data-testid="eat-full-page">
          {t('eat.fullPage')}<ArrowTopRightOnSquareIcon className="h-4 w-4" aria-hidden="true" />
        </Link>
      </header>

      {/* VH-297: the Nutrition tabs, so Recipes and the shopping list are one tap away here too. */}
      <div className="-mx-4">
        <GroupTabs />
      </div>

      <nav className="flex items-center justify-between gap-2" aria-label={t('eat.day')} data-testid="eat-day">
        <button type="button" onClick={() => setDate(shiftIsoDate(date, -1))} data-testid="eat-day-prev"
          aria-label={t('eat.previousDay')} className="rounded-full border border-gray-300 p-3 text-gray-700">
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
        </button>
        <div className="text-center">
          <p className="text-base font-semibold text-gray-900" data-testid="eat-day-label">{dayLabel}</p>
          {!isToday && (
            <button type="button" onClick={() => setDate(today)} data-testid="eat-day-today"
              className="text-sm text-teal-800 underline">
              {t('eat.backToToday')}
            </button>
          )}
        </div>
        <button type="button" onClick={() => setDate(shiftIsoDate(date, 1))} disabled={isToday} data-testid="eat-day-next"
          aria-label={t('eat.nextDay')} className="rounded-full border border-gray-300 p-3 text-gray-700 disabled:opacity-40">
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </nav>

      {meals.isError
        ? <p className="text-sm text-red-700" role="alert">{t('eat.loadFailed')}</p>
        : <IntakeSummary totals={totalsOf(list)} goal={goal.data} meals={list} title={dayLabel} isToday={isToday} />}
      <RecentMeals key={date} date={date} onLogged={refresh} />
      <NextPlannedMeal plan={planForDate(plans.data, date)} date={date} onLogged={refresh} />
      <QuickEntry date={date} defaultMealType={mealTypeForHour(new Date().getHours())} onLogged={refresh} />
    </main>
  )
}
